/** PROTOTYPE. Facts and moments read from the persisted CMMD lifecycle receipt. */
import type {
  CmmdCloudProjectLifecyclePreflight,
  CmmdRuntimeDiagnosticsSnapshot,
} from "@cmmd-forge/contracts";

import { formatTimestamp, shortId, state } from "./diagnosticsPrototypeFormat";
import type { PrototypeFactGroup, PrototypeMoment } from "./diagnosticsPrototypeTypes";

type Receipt = CmmdCloudProjectLifecyclePreflight | undefined;
type Service = NonNullable<
  NonNullable<CmmdCloudProjectLifecyclePreflight["runtime"]>["supervisedService"]
>;

function serviceOf(receipt: Receipt): Service | null {
  return receipt?.runtime?.supervisedService ?? null;
}

function publishedValue(receipt: Receipt): string {
  const inventory = receipt?.inventory;
  if (!inventory) return "not published";
  return `${inventory.projectCount} projects, ${inventory.threadCount} threads at ${formatTimestamp(inventory.publishedAt)}`;
}

export function receiptGroup(receipt: Receipt): PrototypeFactGroup {
  const service = serviceOf(receipt);
  const removal = receipt?.removal ?? null;
  return {
    id: "receipt",
    title: "Lifecycle receipt",
    source: receipt
      ? `Persisted CMMD receipt for project ${receipt.projectId}, ${formatTimestamp(receipt.observedAt)}`
      : "No persisted CMMD receipt",
    observedAt: receipt?.observedAt ?? null,
    facts: [
      { label: "Desired state", value: state(receipt?.runtime?.desiredState) },
      {
        label: "Supervised service",
        value: service ? `${service.name} ${state(service.status)}` : "not reported",
      },
      { label: "Last heartbeat", value: formatTimestamp(service?.lastHeartbeatAt) },
      { label: "Published", value: publishedValue(receipt) },
      { label: "Registered", value: formatTimestamp(receipt?.registration.registeredAt) },
      {
        label: "Removal",
        value: removal ? `${removal.status} (${state(removal.phase)})` : "none",
        ...(removal ? { tone: "warning" as const } : {}),
      },
    ],
  };
}

function snapshotMoment(snapshot: CmmdRuntimeDiagnosticsSnapshot | undefined): PrototypeMoment {
  if (!snapshot) {
    return {
      at: null,
      title: "Snapshot observed",
      detail: "CMMD has not returned a snapshot",
      tone: "muted",
    };
  }
  return {
    at: snapshot.availability.observedAt,
    title: "Snapshot observed",
    detail: `Lifecycle ${state(snapshot.lifecycle.state)}, service ${state(snapshot.service.state)}, replication ${state(snapshot.replication.state)}, last failure ${state(snapshot.failure.classification)}`,
    tone: snapshot.failure.classification === "none" ? "ready" : "warning",
  };
}

function heartbeatMoment(service: Service | null): PrototypeMoment {
  if (!service) {
    return { at: null, title: "Service heartbeat", detail: "No heartbeat recorded", tone: "muted" };
  }
  return {
    at: service.lastHeartbeatAt,
    title: "Service heartbeat",
    detail: `${service.name} ${state(service.status)} on ${service.machineId ?? "unknown machine"}`,
    tone: "ready",
  };
}

function inventoryMoment(
  inventory: CmmdCloudProjectLifecyclePreflight["inventory"],
): PrototypeMoment {
  if (!inventory) {
    return {
      at: null,
      title: "Inventory published",
      detail: "Nothing published yet",
      tone: "muted",
    };
  }
  return {
    at: inventory.publishedAt,
    title: "Inventory published",
    detail: `${inventory.projectCount} projects, ${inventory.threadCount} threads, provider catalog ${state(inventory.providerCatalog)}`,
    tone: "ready",
  };
}

function receiptMoments(receipt: CmmdCloudProjectLifecyclePreflight): PrototypeMoment[] {
  return [
    {
      at: receipt.observedAt,
      title: "Receipt observed",
      detail: `Project ${receipt.projectId}: lifecycle ${state(receipt.runtime?.status)}, desired ${state(receipt.runtime?.desiredState)}`,
      tone: "info",
    },
    heartbeatMoment(serviceOf(receipt)),
    inventoryMoment(receipt.inventory),
    {
      at: receipt.registration.registeredAt,
      title: "Registered",
      detail: `Runtime ${shortId(receipt.runtime?.runtimeId)} registered to project ${receipt.projectId}`,
      tone: "muted",
    },
  ];
}

export function cloudTimeline(
  receipt: Receipt,
  snapshot: CmmdRuntimeDiagnosticsSnapshot | undefined,
): ReadonlyArray<PrototypeMoment> {
  const moments = [snapshotMoment(snapshot), ...(receipt ? receiptMoments(receipt) : [])];
  return moments
    .filter((moment) => moment.at !== null)
    .toSorted((a, b) => (b.at ?? "").localeCompare(a.at ?? ""));
}
